import React, { useState, useEffect, useRef } from 'react';
import {
  getWhatsAppConfig,
  sendToWhatsAppChannel,
  generateWhatsAppShareLink,
  logWhatsAppMessage,
  type WhatsAppConfig,
} from '../../services/whatsappService';
import {
  uploadMultipleFiles,
  validateFiles,
  getFileIcon,
  getFileColor,
  formatFileSize,
  MAX_ATTACHMENTS,
  MAX_FILE_SIZE,
  type UploadedFile,
} from '../../services/fileUploadService';

interface SendAnnouncementFormProps {
  domain: string;
  onSubmit: (formData: {
    title: string;
    message: string;
    targetClass: string;
    priority: 'low' | 'medium' | 'high';
    category: string;
    attachments?: UploadedFile[];
  }) => void;
  onCancel?: () => void;
}

interface PendingFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

const SendAnnouncementForm: React.FC<SendAnnouncementFormProps> = ({
  domain,
  onSubmit,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetClass, setTargetClass] = useState('All Classes');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [category, setCategory] = useState('announcement');
  const [loading, setLoading] = useState(false);

  // Attachment state
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // WhatsApp state
  const [whatsAppConfig, setWhatsAppConfig] = useState<WhatsAppConfig | null>(null);
  const [whatsAppStatus, setWhatsAppStatus] = useState<{
    loading: boolean;
    success?: boolean;
    message?: string;
  }>({ loading: false });

  // Generate classes 1-12 with sections A-D
  const standardClasses = [
    '1-A', '1-B', '1-C', '1-D',
    '2-A', '2-B', '2-C', '2-D',
    '3-A', '3-B', '3-C', '3-D',
    '4-A', '4-B', '4-C', '4-D',
    '5-A', '5-B', '5-C', '5-D',
    '6-A', '6-B', '6-C', '6-D',
    '7-A', '7-B', '7-C', '7-D',
    '8-A', '8-B', '8-C', '8-D',
    '9-A', '9-B', '9-C', '9-D',
    '10-A', '10-B', '10-C', '10-D',
    '11-A', '11-B', '11-C', '11-D',
    '12-A', '12-B', '12-C', '12-D',
  ];

  // Load WhatsApp configuration
  useEffect(() => {
    const loadWhatsAppConfig = async () => {
      if (domain) {
        const config = await getWhatsAppConfig(domain);
        setWhatsAppConfig(config);
      }
    };
    loadWhatsAppConfig();
  }, [domain]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Check current count
    const currentCount = pendingFiles.length + uploadedFiles.length;
    if (currentCount >= MAX_ATTACHMENTS) {
      alert(`Maximum ${MAX_ATTACHMENTS} attachments allowed`);
      return;
    }

    // Validate files
    const { valid, errors } = validateFiles(files);

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    // Add valid files to pending
    const newPendingFiles: PendingFile[] = valid
      .slice(0, MAX_ATTACHMENTS - currentCount)
      .map((file) => ({
        file,
        id: Math.random().toString(36).substring(2, 9),
        progress: 0,
        status: 'pending' as const,
      }));

    setPendingFiles((prev) => [...prev, ...newPendingFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadFiles = async () => {
    if (pendingFiles.length === 0) return;

    setUploading(true);

    const filesToUpload = pendingFiles.filter((pf) => pf.status === 'pending');

    // Update status to uploading
    setPendingFiles((prev) =>
      prev.map((pf) =>
        pf.status === 'pending' ? { ...pf, status: 'uploading' as const } : pf
      )
    );

    const result = await uploadMultipleFiles(
      domain,
      filesToUpload.map((pf) => pf.file),
      'announcements',
      (fileIndex, progress) => {
        setPendingFiles((prev) =>
          prev.map((pf, idx) =>
            idx === fileIndex ? { ...pf, progress: progress.progress } : pf
          )
        );
      }
    );

    // Update state with results
    setUploadedFiles((prev) => [...prev, ...result.files]);

    // Remove successfully uploaded files from pending
    setPendingFiles((prev) =>
      prev.filter((pf) => {
        const wasUploaded = result.files.some(
          (uf) => uf.originalName === pf.file.name
        );
        return !wasUploaded;
      })
    );

    // Show errors if any
    if (result.errors.length > 0) {
      setPendingFiles((prev) =>
        prev.map((pf) => {
          const error = result.errors.find((e) => e.includes(pf.file.name));
          if (error) {
            return { ...pf, status: 'error' as const, error };
          }
          return pf;
        })
      );
    }

    setUploading(false);
  };

  const handleRemovePendingFile = (id: string) => {
    setPendingFiles((prev) => prev.filter((pf) => pf.id !== id));
  };

  const handleRemoveUploadedFile = (id: string) => {
    setUploadedFiles((prev) => prev.filter((uf) => uf.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    // Upload pending files first if any
    if (pendingFiles.filter((pf) => pf.status === 'pending').length > 0) {
      await handleUploadFiles();
    }

    setLoading(true);

    // Send announcement through the main system
    onSubmit({
      title,
      message,
      targetClass,
      priority,
      category,
      attachments: uploadedFiles,
    });

    // Automatically post to WhatsApp channel if configured
    if (whatsAppConfig?.enabled) {
      setWhatsAppStatus({ loading: true });

      const result = await sendToWhatsAppChannel(domain, {
        title,
        content: message,
        category,
        priority,
        attachments: uploadedFiles.map((f) => ({
          originalName: f.originalName,
          url: f.url,
        })),
      });

      setWhatsAppStatus({
        loading: false,
        success: result.success,
        message: result.success
          ? 'Posted to WhatsApp Channel!'
          : result.error || 'Failed to post to WhatsApp',
      });

      // Log the result
      await logWhatsAppMessage(domain, `announcement_${Date.now()}`, result);
    }

    // Reset form
    setTitle('');
    setMessage('');
    setTargetClass('All Classes');
    setPriority('medium');
    setCategory('announcement');
    setPendingFiles([]);
    setUploadedFiles([]);
    setLoading(false);
  };

  const handleShareToWhatsApp = () => {
    if (!title.trim() || !message.trim()) {
      alert('Please enter a title and message first');
      return;
    }

    const shareLink = generateWhatsAppShareLink({
      title,
      content: message,
      category,
    });

    window.open(shareLink, '_blank');
  };

  const totalAttachments = pendingFiles.length + uploadedFiles.length;

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-primary-custom text-white">
        <h5 className="mb-0">
          <i className="bi bi-megaphone me-2"></i>Send Announcement
        </h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., Parent-Teacher Meeting"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Message */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Message <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Enter your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            ></textarea>
            <small className="text-muted">{message.length} characters</small>
          </div>

          {/* Attachments Section */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              <i className="bi bi-paperclip me-2"></i>
              Attachments
              <span className="text-muted fw-normal ms-2">
                ({totalAttachments}/{MAX_ATTACHMENTS})
              </span>
            </label>

            {/* File Input */}
            <div className="d-flex gap-2 mb-2">
              <input
                ref={fileInputRef}
                type="file"
                className="form-control"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileSelect}
                disabled={totalAttachments >= MAX_ATTACHMENTS || uploading}
              />
              {pendingFiles.filter((pf) => pf.status === 'pending').length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleUploadFiles}
                  disabled={uploading}
                >
                  {uploading ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <i className="bi bi-cloud-upload"></i>
                  )}
                </button>
              )}
            </div>

            <small className="text-muted d-block mb-2">
              Allowed: Images, PDF, Word, Excel, PowerPoint (Max {MAX_FILE_SIZE / (1024 * 1024)}MB each)
            </small>

            {/* Pending Files */}
            {pendingFiles.length > 0 && (
              <div className="mb-2">
                {pendingFiles.map((pf) => (
                  <div
                    key={pf.id}
                    className="d-flex align-items-center gap-2 p-2 bg-light rounded mb-1"
                  >
                    <i
                      className={`${getFileIcon(pf.file.type)} fs-5`}
                      style={{ color: getFileColor(pf.file.type) }}
                    ></i>
                    <div className="flex-grow-1">
                      <div className="small fw-semibold text-truncate" style={{ maxWidth: 200 }}>
                        {pf.file.name}
                      </div>
                      <div className="small text-muted">
                        {formatFileSize(pf.file.size)}
                      </div>
                      {pf.status === 'uploading' && (
                        <div className="progress mt-1" style={{ height: 4 }}>
                          <div
                            className="progress-bar"
                            style={{ width: `${pf.progress}%` }}
                          ></div>
                        </div>
                      )}
                      {pf.status === 'error' && (
                        <div className="small text-danger">{pf.error}</div>
                      )}
                    </div>
                    <span
                      className={`badge ${
                        pf.status === 'pending'
                          ? 'bg-secondary'
                          : pf.status === 'uploading'
                          ? 'bg-primary'
                          : pf.status === 'error'
                          ? 'bg-danger'
                          : 'bg-success'
                      }`}
                    >
                      {pf.status}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemovePendingFile(pf.id)}
                      disabled={pf.status === 'uploading'}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <div className="mb-2">
                <small className="text-success fw-bold d-block mb-1">
                  <i className="bi bi-check-circle me-1"></i>
                  Uploaded ({uploadedFiles.length})
                </small>
                {uploadedFiles.map((uf) => (
                  <div
                    key={uf.id}
                    className="d-flex align-items-center gap-2 p-2 bg-success bg-opacity-10 rounded mb-1"
                  >
                    <i
                      className={`${getFileIcon(uf.type)} fs-5`}
                      style={{ color: getFileColor(uf.type) }}
                    ></i>
                    <div className="flex-grow-1">
                      <div className="small fw-semibold text-truncate" style={{ maxWidth: 200 }}>
                        {uf.originalName}
                      </div>
                      <div className="small text-muted">
                        {formatFileSize(uf.size)}
                      </div>
                    </div>
                    <a
                      href={uf.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="bi bi-eye"></i>
                    </a>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemoveUploadedFile(uf.id)}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Target Class */}
          <div className="mb-3">
            <label className="form-label fw-bold">Target Class</label>
            <select
              className="form-select"
              value={targetClass}
              onChange={(e) => setTargetClass(e.target.value)}
            >
              <option value="All Classes">All Classes</option>
              {standardClasses.map((cls) => (
                <option key={cls} value={cls}>
                  Class {cls}
                </option>
              ))}
            </select>
          </div>

          <div className="row">
            {/* Priority */}
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Priority</label>
              <select
                className="form-select"
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {/* Category */}
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Category</label>
              <select
                className="form-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="announcement">General Announcement</option>
                <option value="teacher_announcement">Teacher Announcement</option>
                <option value="fee">Fee Related</option>
                <option value="exam">Exam Related</option>
                <option value="assignment">Assignment</option>
                <option value="result">Result</option>
              </select>
            </div>
          </div>

          {/* WhatsApp Integration Section */}
          <div className={`card mb-3 ${whatsAppConfig?.enabled ? 'bg-success bg-opacity-10 border-success' : 'bg-light'}`}>
            <div className="card-body py-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className={`bi bi-whatsapp fs-4 me-3 ${whatsAppConfig?.enabled ? 'text-success' : 'text-muted'}`}></i>
                  <div>
                    <h6 className="mb-0">
                      WhatsApp Channel
                      {whatsAppConfig?.enabled && (
                        <span className="badge bg-success ms-2">Auto-Post Enabled</span>
                      )}
                    </h6>
                    <small className={whatsAppConfig?.enabled ? 'text-success' : 'text-muted'}>
                      {whatsAppConfig?.enabled
                        ? 'This announcement will be automatically posted to your WhatsApp Channel'
                        : 'WhatsApp integration not configured'}
                    </small>
                  </div>
                </div>

                {!whatsAppConfig?.enabled && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleShareToWhatsApp}
                    disabled={!title.trim() || !message.trim()}
                  >
                    <i className="bi bi-share me-1"></i>
                    Share via WhatsApp
                  </button>
                )}
              </div>

              {/* WhatsApp Status */}
              {whatsAppStatus.message && (
                <div
                  className={`mt-2 small ${
                    whatsAppStatus.success ? 'text-success' : 'text-danger'
                  }`}
                >
                  <i
                    className={`bi ${
                      whatsAppStatus.success ? 'bi-check-circle' : 'bi-exclamation-circle'
                    } me-1`}
                  ></i>
                  {whatsAppStatus.message}
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="d-flex gap-2 flex-wrap">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || uploading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Sending...
                </>
              ) : (
                <>
                  <i className="bi bi-megaphone me-2"></i>
                  Send Announcement
                  {uploadedFiles.length > 0 && (
                    <span className="badge bg-info ms-2">
                      <i className="bi bi-paperclip"></i> {uploadedFiles.length}
                    </span>
                  )}
                  {whatsAppConfig?.enabled && (
                    <span className="badge bg-success ms-2">
                      <i className="bi bi-whatsapp"></i> +WA
                    </span>
                  )}
                </>
              )}
            </button>

            {!whatsAppConfig?.enabled && (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleShareToWhatsApp}
                disabled={!title.trim() || !message.trim()}
              >
                <i className="bi bi-whatsapp me-2"></i>
                Share to WhatsApp
              </button>
            )}

            {onCancel && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendAnnouncementForm;
